import { useEffect, useState } from "react";
import { api, useApi } from "../api.js";
import { Notice, formatDuration, useToast } from "../components.js";

interface CollectionSummary {
  id: number;
  name: string;
  model: string;
  vectorStoreId: string | null;
}

interface KnowledgeSourceRow {
  id: number;
  filename: string;
  roleTag: string;
  indexingStatus: string;
}

interface PlaygroundResponse {
  executionMode: "sync" | "batch";
  message?: string;
  result?: {
    content: string;
    model: string;
    inputTokens: number;
    outputTokens: number;
    cachedTokens: number;
    durationMs: number;
    retrievedChunks: number;
    searchCalls: number;
    notInTextbook: boolean;
    ungrounded: boolean;
    systemFingerprint?: string;
  };
  cost?: number;
  requestMetadata?: {
    requestedModel: string;
    actualModel: string;
    executionMode: string;
    attachedSourcesCount: number;
    responseType: string;
    hyperparameters: {
      temperature: number;
      topP: number;
      frequencyPenalty: number;
      presencePenalty: number;
      reasoningEffort: string;
      seed: number | null;
      maxTokens: number | null;
    };
  };
}

export function Playground() {
  const { toast, element } = useToast();
  const collections = useApi<CollectionSummary[]>("/collections");
  
  const [model, setModel] = useState("gpt-4o");
  const [executionMode, setExecutionMode] = useState<"sync" | "batch">("sync");
  const [collectionId, setCollectionId] = useState<number | null>(null);
  const [selectedSources, setSelectedSources] = useState<Set<number>>(new Set());

  // Hyperparameters
  const [temperature, setTemperature] = useState(0.7);
  const [topP, setTopP] = useState(1.0);
  const [frequencyPenalty, setFrequencyPenalty] = useState(0.0);
  const [presencePenalty, setPresencePenalty] = useState(0.0);
  const [reasoningEffort, setReasoningEffort] = useState<"low" | "medium" | "high">("medium");
  const [seed, setSeed] = useState("");
  const [maxTokens, setMaxTokens] = useState("");

  // Prompt & Inputs
  const [responseType, setResponseType] = useState<"html" | "json" | "markdown">("html");
  const [systemPrompt, setSystemPrompt] = useState(
    "Explain Coulomb's Law of Electrostatics with mathematical formulas, vector forms, and key SI units.",
  );

  // Output & Telemetry
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [response, setResponse] = useState<PlaygroundResponse | null>(null);
  const [activeTab, setActiveTab] = useState<"preview" | "raw" | "metadata">("preview");

  const knowledgeSources = useApi<KnowledgeSourceRow[]>(
    collectionId ? `/collections/${collectionId}/knowledge-sources` : null,
    [collectionId],
  );

  const isReasoningModel =
    model.startsWith("o1") ||
    model.startsWith("o3") ||
    model.startsWith("o4") ||
    model.toLowerCase().includes("luna") ||
    model.toLowerCase().includes("sol") ||
    model.toLowerCase().includes("gpt-5") ||
    model.toLowerCase().includes("reasoner");

  // Prevent accidental page refresh during in-flight generation
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (generating) {
        e.preventDefault();
        e.returnValue = "A generation request is currently in progress. Refreshing now will cancel the response.";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [generating]);

  const applyPreset = (preset: "derivation" | "mcq" | "prose" | "critique" | "schema" | "default") => {
    if (preset === "default") {
      setTemperature(1.0);
      setTopP(1.0);
      setFrequencyPenalty(0.0);
      setPresencePenalty(0.0);
      setReasoningEffort("medium");
      setSeed("");
      setMaxTokens("");
    } else if (preset === "derivation") {
      setTemperature(0.0);
      setTopP(1.0);
      setFrequencyPenalty(0.0);
      setPresencePenalty(0.0);
      setReasoningEffort("high");
      setSeed("42");
      setMaxTokens("2000");
    } else if (preset === "mcq") {
      setTemperature(0.2);
      setTopP(1.0);
      setFrequencyPenalty(0.1);
      setPresencePenalty(0.0);
      setSeed("42");
      setMaxTokens("1000");
    } else if (preset === "prose") {
      setTemperature(0.6);
      setTopP(0.9);
      setFrequencyPenalty(0.1);
      setPresencePenalty(0.2);
      setSeed("");
      setMaxTokens("1500");
    } else if (preset === "critique") {
      setTemperature(0.0);
      setTopP(1.0);
      setFrequencyPenalty(0.0);
      setPresencePenalty(0.0);
      setReasoningEffort("medium");
      setSeed("42");
      setMaxTokens("800");
    } else if (preset === "schema") {
      setTemperature(0.0);
      setTopP(1.0);
      setFrequencyPenalty(0.0);
      setPresencePenalty(0.0);
      setSeed("42");
      setMaxTokens("3000");
    }
    toast(`Loaded preset: ${preset}`);
  };

  const toggleSource = (id: number) => {
    setSelectedSources((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  async function handleFileUpload(file: File) {
    if (!collectionId) {
      toast("Please select a collection to upload and index files into its vector store.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("roleTag", "TEXTBOOK");

      const uploaded = await api.post<KnowledgeSourceRow>(
        `/collections/${collectionId}/knowledge-sources`,
        formData,
      );
      toast(`File "${file.name}" uploaded! Indexing in background…`);
      setSelectedSources((prev) => new Set(prev).add(uploaded.id));
    } catch (err: unknown) {
      toast(err instanceof Error ? err.message : "File upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function handleGenerate() {
    if (!systemPrompt.trim()) {
      toast("Please enter prompt instructions");
      return;
    }

    setGenerating(true);
    setResponse(null);

    try {
      const payload = {
        model,
        executionMode,
        collectionId,
        systemPrompt: systemPrompt.trim(),
        responseType,
        knowledgeSourceIds: Array.from(selectedSources),
        temperature: isReasoningModel ? null : temperature,
        topP: isReasoningModel ? null : topP,
        frequencyPenalty: isReasoningModel ? null : frequencyPenalty,
        presencePenalty: isReasoningModel ? null : presencePenalty,
        reasoningEffort: isReasoningModel ? reasoningEffort : null,
        seed: seed.trim() === "" ? null : Number(seed),
        maxTokens: maxTokens.trim() === "" ? null : Number(maxTokens),
      };

      const res = await api.post<PlaygroundResponse>("/playground/run", payload);
      setResponse(res);
      setActiveTab("preview");
      toast(res.executionMode === "batch" ? "Batch job enqueued" : "Generation complete!");
    } catch (err: unknown) {
      toast(err instanceof Error ? err.message : "Playground generation failed");
    } finally {
      setGenerating(false);
    }
  }

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="row between" style={{ alignItems: "center" }}>
          <div>
            <h1 style={{ margin: 0, fontSize: "1.4rem" }}>🧪 AI Generation Playground</h1>
            <p className="mini muted" style={{ margin: 0 }}>
              Standalone interactive model lab with live parameters, vector store grounding, and response telemetry.
            </p>
          </div>
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className={`btn mini ${executionMode === "sync" ? "primary" : ""}`}
              onClick={() => setExecutionMode("sync")}
            >
              ⚡ Synchronous
            </button>
            <button
              type="button"
              className={`btn mini ${executionMode === "batch" ? "primary" : ""}`}
              onClick={() => setExecutionMode("batch")}
            >
              📦 OpenAI Batch API
            </button>
          </div>
        </div>
      </div>

      <div className="grid">
        {/* Left Column: Configuration & Hyperparameters */}
        <div className="card span-4">
          <h3 style={{ marginTop: 0 }}>1. Model & Engine Controls</h3>

          <div className="field">
            <label>Model</label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}
            >
              <option value="gpt-4o">gpt-4o (Standard Flagship)</option>
              <option value="gpt-4o-mini">gpt-4o-mini (Fast & Cost-Efficient)</option>
              <option value="gpt-5.6-luna">gpt-5.6-luna (Luna Model)</option>
              <option value="o3-mini">o3-mini (Reasoning Model)</option>
              <option value="o1">o1 (Advanced Reasoning)</option>
              <option value="o3">o3 (Deep Reasoning)</option>
            </select>
          </div>

          <div className="field">
            <label>Collection (Vector Store Grounding)</label>
            <select
              value={collectionId ?? ""}
              onChange={(e) => {
                const val = e.target.value ? Number(e.target.value) : null;
                setCollectionId(val);
                setSelectedSources(new Set());
              }}
              style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}
            >
              <option value="">None — Direct Model Generation</option>
              {collections.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.vectorStoreId ? "Indexed" : "No Vector Store"})
                </option>
              ))}
            </select>
          </div>

          {collectionId && (
            <div className="field">
              <div className="row between" style={{ alignItems: "center", marginBottom: 6 }}>
                <label style={{ margin: 0 }}>Attach Library Files</label>
                <label className="btn mini secondary" style={{ cursor: "pointer", margin: 0 }}>
                  {uploading ? "⏳ Uploading..." : "📎 Upload File"}
                  <input
                    type="file"
                    accept=".pdf,.docx,.txt"
                    style={{ display: "none" }}
                    disabled={uploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void handleFileUpload(file);
                    }}
                  />
                </label>
              </div>
              {knowledgeSources.loading ? (
                <span className="mini muted">Loading library sources…</span>
              ) : (knowledgeSources.data ?? []).length === 0 ? (
                <span className="mini muted">No ready sources found in this collection. Upload a file above!</span>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 140, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 6, padding: 6 }}>
                  {knowledgeSources.data
                    ?.filter((ks) => ks.indexingStatus === "ready")
                    .map((ks) => (
                      <label key={ks.id} className="row mini" style={{ cursor: "pointer", gap: 6, alignItems: "center" }}>
                        <input
                          type="checkbox"
                          checked={selectedSources.has(ks.id)}
                          onChange={() => toggleSource(ks.id)}
                        />
                        <span className="truncate" style={{ flex: 1 }}>{ks.filename}</span>
                        <span className="badge mini">{ks.roleTag}</span>
                      </label>
                    ))}
                </div>
              )}
            </div>
          )}

          <h3 style={{ marginTop: 20 }}>2. Hyperparameter Presets</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 16 }}>
            <button type="button" className="btn mini" onClick={() => applyPreset("derivation")}>🔬 Derivation</button>
            <button type="button" className="btn mini" onClick={() => applyPreset("mcq")}>❓ MCQ</button>
            <button type="button" className="btn mini" onClick={() => applyPreset("prose")}>📖 Prose</button>
            <button type="button" className="btn mini" onClick={() => applyPreset("critique")}>🔍 Critique</button>
            <button type="button" className="btn mini" onClick={() => applyPreset("schema")}>⚡ Schema</button>
            <button type="button" className="btn mini" onClick={() => applyPreset("default")}>🔄 Defaults</button>
          </div>

          {!isReasoningModel ? (
            <>
              <div className="field">
                <div className="row between" style={{ alignItems: "center" }}>
                  <label style={{ margin: 0 }}>Temperature ({temperature})</label>
                </div>
                <input
                  type="range"
                  min="0.0"
                  max="2.0"
                  step="0.05"
                  value={temperature}
                  onChange={(e) => setTemperature(Number(e.target.value))}
                  style={{ width: "100%", marginTop: 4 }}
                />
              </div>

              <div className="field">
                <div className="row between" style={{ alignItems: "center" }}>
                  <label style={{ margin: 0 }}>Top P ({topP})</label>
                </div>
                <input
                  type="range"
                  min="0.0"
                  max="1.0"
                  step="0.05"
                  value={topP}
                  onChange={(e) => setTopP(Number(e.target.value))}
                  style={{ width: "100%", marginTop: 4 }}
                />
              </div>

              <div className="field">
                <div className="row between" style={{ alignItems: "center" }}>
                  <label style={{ margin: 0 }}>Frequency Penalty ({frequencyPenalty})</label>
                </div>
                <input
                  type="range"
                  min="-2.0"
                  max="2.0"
                  step="0.1"
                  value={frequencyPenalty}
                  onChange={(e) => setFrequencyPenalty(Number(e.target.value))}
                  style={{ width: "100%", marginTop: 4 }}
                />
              </div>

              <div className="field">
                <div className="row between" style={{ alignItems: "center" }}>
                  <label style={{ margin: 0 }}>Presence Penalty ({presencePenalty})</label>
                </div>
                <input
                  type="range"
                  min="-2.0"
                  max="2.0"
                  step="0.1"
                  value={presencePenalty}
                  onChange={(e) => setPresencePenalty(Number(e.target.value))}
                  style={{ width: "100%", marginTop: 4 }}
                />
              </div>
            </>
          ) : (
            <div style={{ marginBottom: 12 }}>
              <Notice kind="warn">
                Sampling parameters (temperature, top_p) are automatically disabled for <strong>{model}</strong> reasoning model.
              </Notice>
            </div>
          )}

          {isReasoningModel && (
            <div className="field">
              <label>Reasoning Effort</label>
              <select value={reasoningEffort} onChange={(e) => setReasoningEffort(e.target.value as "low" | "medium" | "high")}>
                <option value="low">low — faster, cheaper</option>
                <option value="medium">medium — balanced</option>
                <option value="high">high — maximum deep reasoning</option>
              </select>
            </div>
          )}

          <div className="field">
            <label>Seed (Reproducibility)</label>
            <input
              type="number"
              placeholder="e.g. 42"
              value={seed}
              onChange={(e) => setSeed(e.target.value)}
            />
          </div>

          <div className="field">
            <label>Max Tokens</label>
            <input
              type="number"
              placeholder="e.g. 2000"
              value={maxTokens}
              onChange={(e) => setMaxTokens(e.target.value)}
            />
          </div>
        </div>

        {/* Center/Right Column: Inputs & Interactive Output */}
        <div className="card span-8" style={{ overflow: "hidden" }}>
          <div className="row between" style={{ alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
            <h3 style={{ margin: 0 }}>3. Input Prompt & Format</h3>
            <div style={{ minWidth: 160, maxWidth: "100%", flexShrink: 0 }}>
              <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 2 }}>
                Response Format
              </label>
              <select
                value={responseType}
                onChange={(e) => setResponseType(e.target.value as "html" | "json" | "markdown")}
                style={{ width: "100%", boxSizing: "border-box" }}
              >
                <option value="html">HTML (Rich Content)</option>
                <option value="json">JSON (Structured Schema)</option>
                <option value="markdown">Markdown (Prose)</option>
              </select>
            </div>
          </div>

          <div className="field">
            <label>Prompt Instructions</label>
            <textarea
              rows={6}
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              placeholder="Enter generation prompt or question instructions..."
            />
          </div>

          <button
            type="button"
            className="btn primary full"
            onClick={() => void handleGenerate()}
            disabled={generating}
            style={{ marginBottom: 20, padding: 12, fontSize: "1rem" }}
          >
            {generating ? "⏳ Generating Model Output…" : "🚀 Execute Playground Request"}
          </button>

          {/* Response Output Display */}
          {response && (
            <div>
              <div className="row between" style={{ alignItems: "center", marginBottom: 12, borderBottom: "1px solid var(--border)", paddingBottom: 8 }}>
                <div className="row" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className={`btn mini ${activeTab === "preview" ? "primary" : ""}`}
                    onClick={() => setActiveTab("preview")}
                  >
                    👁️ Clean Render Preview
                  </button>
                  <button
                    type="button"
                    className={`btn mini ${activeTab === "raw" ? "primary" : ""}`}
                    onClick={() => setActiveTab("raw")}
                  >
                    📝 Raw Content
                  </button>
                  <button
                    type="button"
                    className={`btn mini ${activeTab === "metadata" ? "primary" : ""}`}
                    onClick={() => setActiveTab("metadata")}
                  >
                    📊 Request Telemetry
                  </button>
                </div>
                {response.result && (
                  <span className="badge generated mini">
                    {formatDuration(response.result.durationMs)} · ${(response.cost ?? 0).toFixed(4)} USD
                  </span>
                )}
              </div>

              {response.executionMode === "batch" ? (
                <Notice kind="info">
                  <strong>📦 Batch Execution Launcher:</strong> {response.message}
                </Notice>
              ) : activeTab === "preview" ? (
                <div
                  className="content-body"
                  style={{
                    padding: 16,
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                    backgroundColor: "var(--surface)",
                    maxHeight: 450,
                    overflowY: "auto",
                  }}
                  dangerouslySetInnerHTML={{ __html: response.result?.content ?? "" }}
                />
              ) : activeTab === "raw" ? (
                <pre
                  style={{
                    padding: 16,
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                    backgroundColor: "var(--surface)",
                    fontSize: "0.85rem",
                    maxHeight: 450,
                    overflowY: "auto",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {response.result?.content}
                </pre>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div className="card mini">
                    <strong>⏱️ Performance & Telemetry</strong>
                    <hr style={{ margin: "8px 0" }} />
                    <div className="mini"><strong>Execution Mode:</strong> {response.executionMode}</div>
                    <div className="mini"><strong>Requested Model:</strong> {response.requestMetadata?.requestedModel}</div>
                    <div className="mini"><strong>Executed Model:</strong> {response.result?.model}</div>
                    <div className="mini"><strong>Duration:</strong> {response.result?.durationMs} ms</div>
                    <div className="mini"><strong>Estimated Cost:</strong> ${response.cost?.toFixed(5)} USD</div>
                    <div className="mini"><strong>System Fingerprint:</strong> <code>{response.result?.systemFingerprint ?? "n/a"}</code></div>
                  </div>

                  <div className="card mini">
                    <strong>🔤 Token & Grounding Evidence</strong>
                    <hr style={{ margin: "8px 0" }} />
                    <div className="mini"><strong>Input Tokens:</strong> {response.result?.inputTokens}</div>
                    <div className="mini"><strong>Output Tokens:</strong> {response.result?.outputTokens}</div>
                    <div className="mini"><strong>Cached Tokens:</strong> {response.result?.cachedTokens}</div>
                    <div className="mini"><strong>Vector Search Calls:</strong> {response.result?.searchCalls}</div>
                    <div className="mini"><strong>Retrieved Chunks:</strong> {response.result?.retrievedChunks}</div>
                    <div className="mini">
                      <strong>Grounding Status:</strong>{" "}
                      {response.result?.notInTextbook
                        ? "Not In Textbook"
                        : response.result?.ungrounded
                        ? "Ungrounded"
                        : "Grounded"}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {element}
    </>
  );
}
